import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  FileText, Download, Loader2, Plus, Printer, Send, Upload, CheckCircle, Eye, Edit,
  FileSignature, Copy, MailCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const statusConfig = {
  draft: { label: "Brouillon", color: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  sent: { label: "Envoye", color: "bg-[#3B82F6]/10 text-[#3B82F6]" },
  signed: { label: "Signe", color: "bg-[#10B981]/10 text-[#10B981]" },
  active: { label: "Actif", color: "bg-[#0099CC]/10 text-[#0099CC]" },
  expired: { label: "Expire", color: "bg-[#64748b]/10 text-[#94A3B8]" },
};

export default function ServiceContractsPage() {
  const { isAdmin } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanContractId, setScanContractId] = useState(null);
  const [scanUploading, setScanUploading] = useState(false);
  const [sending, setSending] = useState(null);
  const [sigSending, setSigSending] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateClientId, setTemplateClientId] = useState("");
  const [viewContract, setViewContract] = useState(null);
  const [form, setForm] = useState({
    client_id: "", title: "", services: "", duration: "", amount: "", conditions: "",
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, clRes, tRes] = await Promise.all([
        axios.get(`${API}/service-contracts`),
        isAdmin ? axios.get(`${API}/clients`) : Promise.resolve({ data: [] }),
        isAdmin ? axios.get(`${API}/contract-templates`) : Promise.resolve({ data: [] }),
      ]);
      setContracts(cRes.data);
      setClients(clRes.data);
      setTemplates(tRes.data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const openCreateDialog = () => {
    setEditContract(null);
    setForm({ client_id: "", title: "", services: "", duration: "", amount: "", conditions: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (c) => {
    setEditContract(c);
    setForm({
      client_id: c.client_id,
      title: c.title,
      services: c.services,
      duration: c.duration || "",
      amount: c.amount || "",
      conditions: c.conditions || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.title || !form.services) {
      toast.error("Remplissez les champs obligatoires");
      return;
    }
    try {
      const payload = { ...form, amount: form.amount ? parseFloat(form.amount) : 0 };
      if (editContract) {
        await axios.put(`${API}/service-contracts/${editContract.id}`, payload);
        toast.success("Contrat mis a jour");
      } else {
        await axios.post(`${API}/service-contracts`, payload);
        toast.success("Contrat cree");
      }
      setDialogOpen(false);
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const handlePrint = async (contractId) => {
    try {
      const res = await axios.get(`${API}/service-contracts/${contractId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const w = window.open(url);
      if (w) {
        w.onload = () => { w.print(); };
      }
    } catch { toast.error("Erreur generation PDF"); }
  };

  const handleDownload = async (contractId) => {
    try {
      const res = await axios.get(`${API}/service-contracts/${contractId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrat-${contractId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Erreur telechargement"); }
  };

  const handleSendEmail = async (contractId) => {
    setSending(contractId);
    try {
      await axios.post(`${API}/service-contracts/${contractId}/send-email`);
      toast.success("Contrat envoye par email");
      fetchAll();
    } catch { toast.error("Erreur envoi email"); }
    finally { setSending(null); }
  };

  const openScanDialog = (contractId) => {
    setScanContractId(contractId);
    setScanDialogOpen(true);
  };

  const handleScanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !scanContractId) return;
    setScanUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/service-contracts/${scanContractId}/upload-scan`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Scan uploade, contrat marque comme signe");
      setScanDialogOpen(false);
      fetchAll();
    } catch { toast.error("Erreur upload scan"); }
    finally { setScanUploading(false); }
  };

  const handleViewScan = async (contractId) => {
    window.open(`${API}/service-contracts/${contractId}/scan`, "_blank");
  };

  const handleSendSignature = async (contractId) => {
    setSigSending(contractId);
    try {
      const res = await axios.post(`${API}/contracts/${contractId}/send-signature-request`);
      toast.success("Demande de signature envoyee par email");
      fetchAll();
    } catch { toast.error("Erreur envoi signature"); }
    finally { setSigSending(null); }
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate || !templateClientId) return;
    try {
      await axios.post(`${API}/service-contracts/from-template`, {
        template_id: selectedTemplate,
        client_id: templateClientId,
      });
      toast.success("Contrat cree depuis le modele");
      setTemplateDialog(false);
      setSelectedTemplate("");
      setTemplateClientId("");
      fetchAll();
    } catch { toast.error("Erreur creation"); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="service-contracts-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Contrats de services</h1>
          <p className="text-[#94A3B8] mt-1">{contracts.length} contrat{contracts.length > 1 ? "s" : ""}</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTemplateDialog(true)}
              className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="from-template-btn">
              <Copy className="h-4 w-4 mr-2" /> Depuis un modele
            </Button>
            <Button onClick={openCreateDialog} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="new-contract-btn">
              <Plus className="h-4 w-4 mr-2" /> Nouveau contrat
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : contracts.length === 0 ? (
        <div className="text-center py-20 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <FileText className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun contrat</h3>
          <p className="text-[#94A3B8]">Creez votre premier contrat de services</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {contracts.map(c => {
            const st = statusConfig[c.status] || statusConfig.draft;
            return (
              <div key={c.id} className={`bg-[#151B2B] border border-[#2A3441] rounded-xl p-5 hover:border-[#2A3441]/80 transition-colors ${!isAdmin ? "cursor-pointer hover:border-[#0099CC]/30" : ""}`}
                onClick={() => !isAdmin && setViewContract(c)}
                data-testid={`contract-${c.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-semibold text-lg truncate">{c.title}</h3>
                      <Badge className={`${st.color} border-0`}>{st.label}</Badge>
                      {c.signed_manually && <Badge className="bg-[#10B981]/10 text-[#10B981] border-0">Signe manuellement</Badge>}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                      <span>{c.client_name || "Client"}</span>
                      {c.amount > 0 && <span className="text-[#0099CC] font-semibold">{c.amount.toLocaleString("fr-FR")} EUR HT</span>}
                      {c.duration && <span>{c.duration}</span>}
                      <span>{formatDate(c.created_at)}</span>
                    </div>
                    <p className="text-[#64748b] text-sm mt-2 line-clamp-2">{c.services}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => handleDownload(c.id)}
                      className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid={`download-${c.id}`}>
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handlePrint(c.id)}
                      className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid={`print-${c.id}`}>
                      <Printer className="h-4 w-4 mr-1" /> Imprimer
                    </Button>
                    {isAdmin && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => handleSendEmail(c.id)} disabled={sending === c.id}
                          className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid={`send-email-${c.id}`}>
                          {sending === c.id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Email
                        </Button>
                        {!c.signed_manually && c.status !== "signed" && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => handleSendSignature(c.id)} disabled={sigSending === c.id}
                              className="border-purple-500/50 text-purple-400 hover:text-purple-300" data-testid={`esign-${c.id}`}>
                              {sigSending === c.id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MailCheck className="h-4 w-4 mr-1" />} E-signature
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => openScanDialog(c.id)}
                              className="border-emerald-500/50 text-emerald-400 hover:text-emerald-300" data-testid={`upload-scan-${c.id}`}>
                              <Upload className="h-4 w-4 mr-1" /> Signe manuellement
                            </Button>
                          </>
                        )}
                        {c.scan_path && (
                          <Button variant="outline" size="sm" onClick={() => handleViewScan(c.id)}
                            className="border-[#2A3441] text-[#94A3B8] hover:text-white">
                            <Eye className="h-4 w-4 mr-1" /> Voir scan
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(c)}
                          className="text-[#94A3B8] hover:text-white">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white">{editContract ? "Modifier le contrat" : "Nouveau contrat de services"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm(p => ({ ...p, client_id: v }))}>
                <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="contract-client-select">
                  <SelectValue placeholder="Selectionnez un client" />
                </SelectTrigger>
                <SelectContent className="bg-[#151B2B] border-[#2A3441] max-h-60">
                  {clients.map(cl => <SelectItem key={cl.id} value={cl.id}>{cl.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Titre du contrat *</Label>
              <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Ex: Contrat de maintenance site web" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="contract-title" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Services inclus *</Label>
              <Textarea value={form.services} onChange={(e) => setForm(p => ({ ...p, services: e.target.value }))}
                placeholder="Detaillez les services couverts par ce contrat..." rows={3}
                className="bg-[#0B0F19] border-[#2A3441] text-white resize-none" data-testid="contract-services" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Duree</Label>
                <Input value={form.duration} onChange={(e) => setForm(p => ({ ...p, duration: e.target.value }))}
                  placeholder="Ex: 12 mois" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="contract-duration" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Montant (EUR HT)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))}
                  placeholder="Ex: 2500" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="contract-amount" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Conditions particulieres</Label>
              <Textarea value={form.conditions} onChange={(e) => setForm(p => ({ ...p, conditions: e.target.value }))}
                placeholder="Conditions de paiement, clauses specifiques..." rows={3}
                className="bg-[#0B0F19] border-[#2A3441] text-white resize-none" data-testid="contract-conditions" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button type="submit" className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="save-contract-btn">
                {editContract ? "Mettre a jour" : "Creer le contrat"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Scan Dialog */}
      <Dialog open={scanDialogOpen} onOpenChange={setScanDialogOpen}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Signe manuellement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-[#94A3B8] text-sm">Uploadez le scan du contrat signe. Le contrat sera automatiquement marque comme signe.</p>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Fichier (PDF ou image)</Label>
              <Input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleScanUpload} disabled={scanUploading}
                className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="scan-file-input" />
              {scanUploading && (
                <div className="flex items-center gap-2 text-[#0099CC] text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Upload en cours...
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white">Creer depuis un modele</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Client</Label>
              <Select value={templateClientId} onValueChange={setTemplateClientId}>
                <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="template-client-select">
                  <SelectValue placeholder="Selectionnez un client" />
                </SelectTrigger>
                <SelectContent className="bg-[#151B2B] border-[#2A3441] max-h-60">
                  {clients.map(cl => <SelectItem key={cl.id} value={cl.id}>{cl.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Modele de contrat</Label>
              <div className="grid gap-2">
                {templates.map(t => (
                  <button key={t.id} onClick={() => setSelectedTemplate(t.id)}
                    className={`text-left p-3 rounded-lg border transition-colors ${
                      selectedTemplate === t.id
                        ? "border-[#0099CC] bg-[#0099CC]/10"
                        : "border-[#2A3441] bg-[#0B0F19] hover:border-[#0099CC]/50"
                    }`} data-testid={`template-${t.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm font-medium">{t.name}</span>
                      <span className="text-[#0099CC] text-sm font-semibold">{t.amount} EUR</span>
                    </div>
                    <p className="text-[#64748b] text-xs mt-1">{t.duration}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setTemplateDialog(false)} className="flex-1 border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button onClick={handleCreateFromTemplate} disabled={!selectedTemplate || !templateClientId}
                className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="create-from-template-btn">
                Creer le contrat
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Contract Detail Dialog */}
      <Dialog open={!!viewContract} onOpenChange={(open) => !open && setViewContract(null)}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white">{viewContract?.title}</DialogTitle>
          </DialogHeader>
          {viewContract && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-3">
                <Badge className={`${(statusConfig[viewContract.status] || statusConfig.draft).color} border-0`}>
                  {(statusConfig[viewContract.status] || statusConfig.draft).label}
                </Badge>
                {viewContract.signed_manually && <Badge className="bg-[#10B981]/10 text-[#10B981] border-0">Signe manuellement</Badge>}
              </div>
              <div className="space-y-3 bg-[#0B0F19] rounded-lg p-4 border border-[#2A3441]">
                {viewContract.amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#94A3B8] text-sm">Montant</span>
                    <span className="text-[#0099CC] font-bold">{viewContract.amount.toLocaleString("fr-FR")} EUR HT</span>
                  </div>
                )}
                {viewContract.duration && (
                  <div className="flex justify-between">
                    <span className="text-[#94A3B8] text-sm">Duree</span>
                    <span className="text-white">{viewContract.duration}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#94A3B8] text-sm">Date</span>
                  <span className="text-white">{formatDate(viewContract.created_at)}</span>
                </div>
              </div>
              <div>
                <p className="text-[#94A3B8] text-xs uppercase tracking-wider mb-2">Services inclus</p>
                <p className="text-white text-sm whitespace-pre-wrap">{viewContract.services}</p>
              </div>
              {viewContract.conditions && (
                <div>
                  <p className="text-[#94A3B8] text-xs uppercase tracking-wider mb-2">Conditions</p>
                  <p className="text-white text-sm whitespace-pre-wrap">{viewContract.conditions}</p>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => handleDownload(viewContract.id)}
                  className="flex-1 border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="client-download-contract">
                  <Download className="h-4 w-4 mr-1" /> Telecharger PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setViewContract(null); }}
                  className="border-[#2A3441] text-[#94A3B8]">Fermer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
